import { useMemo } from 'react';

import { InternalLink } from '../../components/Links';
import { HelmetTitle } from '../../providers/helmet';
import { useReturnValue } from '../../providers/returnValue';
import { ROOT_ROUTE } from '../../routes/constants';
import { checkHasBrandingData } from '../../utils/checkHasBrandingData';
import { LeftBrandLogo, LeftWSLogo } from './Header';
import RightMenuV2 from './Header/RightMenuV2';

const PageBaseV2 = ({ titleBase, children }) => {
  const { brandingData } = useReturnValue();

  const hasBranding = useMemo(() => {
    return checkHasBrandingData(brandingData);
  }, [brandingData]);

  const pageData: Record<string, string | JSX.Element | string[]> = {};

  if (hasBranding) {
    // example:
    // "favicon": {
    //   "href": "https://ucarecdn.com/020dbd3a-129c-454c-9d05-a90b2dd365d6/-/resize/32x/",
    //   "rel": "icon",
    //   "sizes": "32x32",
    //   "type": "image/png"
    // },
    const favicons = [brandingData?.favicon, brandingData?.faviconSmall]
      .filter((fav) => fav?.href)
      .map((fav) => {
        const rel = fav?.rel ?? 'icon';
        const type = fav?.type ?? 'image/png';
        const sizes = fav?.sizes ? { sizes: fav.sizes } : {};
        const href = fav?.href;

        return <link key={href} href={href} rel={rel} type={type} {...sizes} />;
      });
    // example:
    // "logo": "https://ucarecdn.com/c1303c54-e765-4c61-bdc0-3922e768c490/-/format/auto/",
    // "logoSmall": "https://ucarecdn.com/2f563cc2-ca9d-4668-b693-f8aed0159112/-/resize/19x/-/quality/smart/"
    Object.assign(pageData, {
      favicons,
      logo: (
        <LeftBrandLogo
          srcBig={brandingData?.logo}
          srcSmall={brandingData?.logoSmall}
        />
      ),
    });
  } else {
    Object.assign(pageData, {
      favicons: [],
      logo: <LeftWSLogo />,
    });
  }

  return (
    <div className="min-h-screen h-full flex flex-col items-center bg-gray-100">
      {/* @ts-expect-error the IDE gets confused over which TS version to use */}
      <HelmetTitle
        title={`${titleBase}${hasBranding ? '' : ' - Wonderschool'}`}
        favicons={pageData.favicons}
      />

      <div className="flex justify-between items-center px-3 pt-3 md:px-6 md:pt-6 w-full space-between">
        <div>
          <InternalLink to={ROOT_ROUTE} text="">
            {pageData.logo as JSX.Element}
          </InternalLink>
        </div>
        <div>
          <RightMenuV2 />
        </div>
      </div>

      <div className="flex flex-col justify-center w-full grow h-full p-3 md:p-6 max-w-screen-sm mx-auto max-sm:px-2">
        {children ?? null}
      </div>

      {/* Filler div so the second gets centered properly */}
      <div className="w-full h-20"></div>
    </div>
  );
};

export default PageBaseV2;
